import { toQuery, type HttpClient } from '../client';
import type { AuditLogQuery, AuditLogRow, AuditSummaryResult } from '../types/audit';
import type { PageResult } from '../types/result';
import { ApiError, httpStatusToCode } from '../types/result';

const BASE = '/sys/audit';

/**
 * 审计日志（`ops_audit_log`）：平台侧跨租户举证查询。
 * 权限：`sys:audit:view`；时间区间由后端换算为主键区间（DR-4.3）。
 */
export function createAuditApi(http: HttpClient) {
  return {
    list(query: AuditLogQuery = {}): Promise<PageResult<AuditLogRow>> {
      return http.request<PageResult<AuditLogRow>>({ path: BASE, query: toQuery(query) });
    },

    /** 按操作类型聚合（举证报表用）；平台侧可按租户收口。 */
    summary(begin?: string, end?: string, tenantId?: string): Promise<AuditSummaryResult> {
      return http.request<AuditSummaryResult>({
        path: `${BASE}/summary`,
        query: toQuery({ begin, end, tenantId })
      });
    },

    /**
     * 导出 CSV（`GET /sys/audit/export`，导出动作本身也写审计，DR-13.5）。
     * 走 `http.send` 原始响应：保留令牌注入、超时、401 统一处理与 X-Request-Id，
     * 避免各站点裸 fetch 再拼一份鉴权逻辑（AIR-1.21）。
     */
    async exportCsv(query: AuditLogQuery = {}): Promise<string> {
      const res = await http.send({ path: `${BASE}/export`, query: toQuery(query) });
      if (res.status >= 400) {
        // 失败时后端返回统一 JSON 信封，尽量把可读原因带出来
        const body = res.body as { message?: string; traceId?: string } | string | null;
        const message = typeof body === 'string' ? undefined : body?.message;
        throw new ApiError({
          code: httpStatusToCode(res.status),
          message: message || `导出失败（HTTP ${res.status}）`,
          httpStatus: res.status,
          traceId: typeof body === 'string' ? null : (body?.traceId ?? null),
          payload: res.body
        });
      }

      // 适配器先按 JSON 解析、失败则原样返回文本：CSV 命中后者
      return typeof res.body === 'string' ? res.body : String(res.body ?? '');
    }
  };
}

export type AuditApi = ReturnType<typeof createAuditApi>;
